import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiError } from './portalApi';
import { T, sans } from './portalTheme';
import { usePortalAuth } from '../../context/PortalAuthContext';
import {
  PortalAppShell, PageContainer, PageHeader, Card, Avatar, StatusPill, DueBadge, ProgressBar, Button, EmptyState,
  Icon, IC, Spinner, usePortalInbox, daysUntil, PortalResponseRow,
} from './PortalWidgets';
import { RatingHero, startClaim } from './portalRatingShared';

type Filter = 'open' | 'submitted' | 'all';
type Sort = 'due' | 'recent';

const isOpen = (r: PortalResponseRow) => r.status === 'sent' || r.status === 'opened';

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

// /questionnaire/portal/dashboard -- every questionnaire sent to this account's email (or that
// they were invited to collaborate on), from any FluxWatch customer, in one inbox.
export default function PortalDashboard() {
  const navigate = useNavigate();
  const { account } = usePortalAuth();
  const inbox = usePortalInbox();
  const responses = useMemo(() => inbox.data?.responses ?? [], [inbox.data]);

  const [filter, setFilter] = useState<Filter>('open');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('due');
  const [claiming, setClaiming] = useState(false);

  const stats = useMemo(() => {
    const open = responses.filter(isOpen);
    return {
      notStarted: responses.filter(r => r.status === 'sent').length,
      inProgress: responses.filter(r => r.status === 'opened').length,
      dueSoon: open.filter(r => r.due_date && daysUntil(r.due_date) >= 0 && daysUntil(r.due_date) <= 7).length,
      overdue: open.filter(r => r.due_date && daysUntil(r.due_date) < 0).length,
      done: responses.filter(r => r.status === 'answered' || r.status === 'reviewed').length,
      open: open.length,
    };
  }, [responses]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = responses.filter(r => {
      if (filter === 'open' && !isOpen(r)) return false;
      if (filter === 'submitted' && isOpen(r)) return false;
      if (q && ![r.questionnaire_name, r.sender_org_name, r.vendor_name].some(v => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      if (sort === 'recent') return b.sent_at.localeCompare(a.sent_at);
      // Soonest due first; open work before finished; no due date last.
      if (isOpen(a) !== isOpen(b)) return isOpen(a) ? -1 : 1;
      return (a.due_date || '9999').localeCompare(b.due_date || '9999');
    });
  }, [responses, filter, search, sort]);

  const firstName = (account?.name || '').split(' ')[0];
  const tiles = [
    { label: 'To do', value: stats.notStarted, sub: 'Not started yet', icon: IC.inbox, color: T.slate, onClick: () => setFilter('open') },
    { label: 'In progress', value: stats.inProgress, sub: 'Started, not submitted', icon: IC.activity, color: T.primary, onClick: () => setFilter('open') },
    { label: stats.overdue ? 'Overdue' : 'Due this week', value: stats.overdue || stats.dueSoon,
      sub: stats.overdue ? `${stats.dueSoon} more due this week` : 'Due in the next 7 days', icon: stats.overdue ? IC.alert : IC.clock,
      color: stats.overdue ? T.red : T.amber, onClick: () => { setFilter('open'); setSort('due'); } },
    { label: 'Submitted', value: stats.done, sub: 'Sent back to requesters', icon: IC.checkCircle, color: T.green, onClick: () => setFilter('submitted') },
  ];

  const segBtn = (key: Filter, label: string, count: number) => {
    const on = filter === key;
    return (
      <button key={key} onClick={() => setFilter(key)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
        fontFamily: sans, fontSize: 13, fontWeight: 600, background: on ? T.surface : 'transparent', color: on ? T.ink : T.slate,
        boxShadow: on ? '0 1px 2px rgba(15,23,42,.1)' : 'none',
      }}>
        {label}
        <span style={{ fontSize: 11.5, fontWeight: 700, color: on ? T.primary : T.faint, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      </button>
    );
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '11px 16px', fontSize: 11.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: T.faint, borderBottom: `1px solid ${T.bdr}`, background: T.surface2, whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '16px', borderBottom: `1px solid ${T.bdr}`, verticalAlign: 'middle' };

  return (
    <PortalAppShell>
      <PageContainer>
        <PageHeader
          title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
          subtitle={inbox.isPending ? 'Loading your questionnaires…'
            : stats.open ? `You have ${stats.open} questionnaire${stats.open === 1 ? '' : 's'} waiting for a response.`
            : 'You\'re all caught up — no open questionnaires right now.'}
        />

        {/* The vendor's own company rating: big on first sign-in, collapsible to one line afterwards. */}
        <RatingHero accountId={account?.id} firstName={firstName} claiming={claiming}
          onClaim={async () => { setClaiming(true); try { await startClaim(); } catch { setClaiming(false); } }} />

        {inbox.isPending ? (
          <Card><div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div></Card>
        ) : inbox.isError ? (
          <Card>
            <EmptyState icon={IC.alert} title="Couldn't load your questionnaires" action={<Button variant="secondary" onClick={() => inbox.refetch()}>Try again</Button>}>
              {apiError(inbox.error, 'Something went wrong. Check your connection and try again.')}
            </EmptyState>
          </Card>
        ) : (
          <>
            <div className="pw-tiles" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 24 }}>
              {tiles.map(t => (
                <button key={t.label} onClick={t.onClick} style={{
                  textAlign: 'left', fontFamily: sans, cursor: 'pointer', background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 12,
                  padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start',
                }}>
                  <span style={{ width: 38, height: 38, borderRadius: 10, background: `${t.color}14`, color: t.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon d={t.icon} size={18} />
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 24, fontWeight: 800, color: T.ink, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{t.value}</span>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.ink2, marginTop: 3 }}>{t.label}</span>
                    <span style={{ display: 'block', fontSize: 12, color: T.muted, marginTop: 1 }}>{t.sub}</span>
                  </span>
                </button>
              ))}
            </div>


            <Card padding={0} style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', flexWrap: 'wrap', borderBottom: `1px solid ${T.bdr}` }}>
                <div style={{ display: 'inline-flex', background: T.hover, borderRadius: 9, padding: 3 }}>
                  {segBtn('open', 'Open', stats.open)}
                  {segBtn('submitted', 'Submitted', stats.done)}
                  {segBtn('all', 'All', responses.length)}
                </div>
                <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 360 }}>
                  <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.faint, display: 'inline-flex' }}><Icon d={IC.search} size={15} /></span>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questionnaires or companies" className="pw-input"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px 8px 34px', fontSize: 13.5, fontFamily: sans, color: T.ink, border: `1px solid ${T.bdr}`, borderRadius: 8, outline: 'none' }} />
                </div>
                <div style={{ flex: 1 }} />
                <select value={sort} onChange={e => setSort(e.target.value as Sort)} aria-label="Sort"
                  style={{ padding: '8px 12px', fontSize: 13, fontFamily: sans, color: T.ink2, background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 8, cursor: 'pointer' }}>
                  <option value="due">Sort: Due date</option>
                  <option value="recent">Sort: Recently received</option>
                </select>
              </div>

              {responses.length === 0 ? (
                <EmptyState icon={IC.inbox} title="No questionnaires yet">
                  When a company sends a security questionnaire to {account?.email}, it will appear here. You'll also get an email with a link.
                </EmptyState>
              ) : rows.length === 0 ? (
                <EmptyState icon={filter === 'open' ? IC.checkCircle : IC.search}
                  title={search ? 'No matches' : filter === 'open' ? 'Nothing waiting on you' : 'Nothing submitted yet'}
                  action={(search || filter !== 'all') ? <Button variant="secondary" onClick={() => { setSearch(''); setFilter('all'); }}>Show all questionnaires</Button> : undefined}>
                  {search ? `No questionnaires match "${search}".` : filter === 'open' ? 'Every questionnaire sent to you has been submitted.' : 'Questionnaires you submit will show up here.'}
                </EmptyState>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                    <thead>
                      <tr>
                        <th style={th}>Questionnaire</th>
                        <th style={th}>Due</th>
                        <th style={{ ...th, width: 190 }}>Progress</th>
                        <th style={th}>Status</th>
                        <th style={{ ...th, textAlign: 'right' }} aria-label="Action" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const action = r.status === 'sent' ? 'Start' : r.status === 'opened' ? 'Continue' : 'View';
                        return (
                          <tr key={r.id} className="pw-row" onClick={() => navigate(`/questionnaire/portal/response/${r.id}`)} style={{ cursor: 'pointer' }}>
                            <td style={td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                <Avatar name={r.sender_org_name} size={38} square />
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{r.questionnaire_name}</span>
                                    {r.is_collaborator && (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: T.violet, background: T.violetBg, borderRadius: 99, padding: '2px 8px' }}>
                                        <Icon d={IC.users} size={11} /> Collaborating
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12.5, color: T.muted, marginTop: 3 }}>
                                    From <strong style={{ color: T.slate, fontWeight: 600 }}>{r.sender_org_name}</strong> · for {r.vendor_name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={td}><DueBadge due={r.due_date} status={r.status} /></td>
                            <td style={td}>
                              <ProgressBar value={r.answered_count} total={r.total_items} showLabel />
                              <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>{r.answered_count} of {r.total_items} answered</div>
                            </td>
                            <td style={td}><StatusPill status={r.status} /></td>
                            <td style={{ ...td, textAlign: 'right' }}>
                              <Button size="sm" variant={action === 'View' ? 'secondary' : 'primary'}
                                onClick={e => { e.stopPropagation(); navigate(`/questionnaire/portal/response/${r.id}`); }}>
                                {action} <Icon d={IC.chevRight} size={13} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </PageContainer>
    </PortalAppShell>
  );
}
